import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ReactPlayer from 'react-player';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link,
  IconButton,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  Drawer
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ProposalList } from '../ProposalComponent/ProposalList';
import { parseApiErrorResponse, validateUploadFile } from '../../utils/uploadLimits';

const normalizeFunding = (v) => (v === 'Graduate Technician/Assistantship' ? 'Assistantship' : v);
const endpointpath = process.env.REACT_APP_API_BASE_URL;

const PROPOSAL_FIELD_LIMITS = Object.freeze({
  degreeProgram: 200,
  experience: 1200,
  proposalTitle: 200,
  unmetRequirements: 800,
  proposalSummary: 1500,
  anticipatedOutcomes: 900,
});

const joinKeywords = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ');
  }
  return value
    .toString()
    .split(/[;,]/)
    .map((keyword) => keyword.trim())
    .filter(Boolean)
    .join(', ');
};

const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
};

const ProjectDetails = ({ project, userType, userInfo, proposalFormPortalRef }) => {
  const location = useLocation();
  const isEditPage = new URLSearchParams(location.search).get('mode') === 'edit';
  const isStudentUser = (userType || '').toLowerCase() === 'student';

  const resolveEmail = (ui) => {
    if (!ui) return '';
    return ui.Email ?? ui.email ?? ui.mail ?? ui.userPrincipalName ?? ui.upn ?? ui.preferred_username ?? '';
  };
  const resolveName = (ui) => {
    if (!ui) return '';
    const first = ui.FirstName ?? ui.firstName ?? ui.first_name ?? ui.given_name ?? ui.givenName ?? '';
    const last  = ui.LastName  ?? ui.lastName  ?? ui.last_name  ?? ui.family_name ?? ui.surname    ?? '';
    const display = ui.DisplayName ?? ui.displayName ?? ui.name ?? ui.Name ?? ui.cn ?? '';
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    if (display) {
      const normalized = display.trim();
      if (normalized.includes(',')) {
        const parts = normalized.split(',').map(part => part.trim()).filter(Boolean);
        if (parts.length === 2) {
          return `${parts[1]} ${parts[0]}`.trim();
        }
      }
      return normalized;
    }
    const email = resolveEmail(ui);
    if (!email) return '';
    const local = email.split('@')[0];
    const pretty = local
      .replace(/[._-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(s => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
    return pretty;
  };

  const sidebarName = resolveName(userInfo);
  const sidebarEmail = resolveEmail(userInfo);
  const sidebarUIN = userInfo?.UIN || '';
  const ReadOnlyField = ({ label, value }) => {
    const resolved =
      value === undefined || value === null || value === '' ? 'N/A' : value;

    return (
      <Box sx={{ mb: 2, minWidth: 0, maxWidth: '100%' }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: '#4a1711',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 0.75,
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            border: '1px solid #c7c7c7',
            borderRadius: 1,
            backgroundColor: '#ffffff',
            px: 2.5,
            py: 1.6,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {React.isValidElement(resolved) ? (
            resolved
          ) : (
            <Typography
              sx={{
                fontSize: '1.05rem',
                fontWeight: 600,
                color: '#1f1f1f',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {resolved}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const [isStudentFormOpen, setIsStudentFormOpen] = useState(false);
  const [proposalPortalMount, setProposalPortalMount] = useState(null);

  const handleStudentFormClose = () => setIsStudentFormOpen(false);

  const handleClick = () => {
    if ((userType || '').toLowerCase() === 'employer') {
      const url = '/project-form?mode=edit&projectID=' + project.projectID;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (isStudentUser) {
      setIsStudentFormOpen((prev) => !prev);
    }
  };

  useLayoutEffect(() => {
    if (!isStudentFormOpen || !isStudentUser) {
      setProposalPortalMount(null);
      return;
    }
    const pick = () =>
      proposalFormPortalRef?.current ??
      (typeof document !== 'undefined'
        ? document.getElementById('student-proposal-overlay-root')
        : null);
    const el = pick();
    if (el) {
      setProposalPortalMount(el);
      return;
    }
    const id = requestAnimationFrame(() => {
      setProposalPortalMount(pick());
    });
    return () => cancelAnimationFrame(id);
  }, [isStudentFormOpen, isStudentUser, proposalFormPortalRef, project?.projectID]);

  useEffect(() => {
    if (!isStudentFormOpen || !isStudentUser) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsStudentFormOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isStudentFormOpen, isStudentUser]);

  const handleViewProposals = () => {
    navigate(`/proposal-viewer/${encodeURIComponent(project.projectID)}`);
  };

  const handleOpen = () => {
    // Logic to open your modal or handle video playback
  };

  const [formData, setFormData] = useState({
      projectID: '', 
      requesterUIN: '',
      requesterName: '',
      requesterTitle: '',
      requesterDepartment: '',
      requesterEmail: '',
      requestTitle: '',
      proposalDeadline: '',
      startDate: '',
      endDate: '',
      keywords: '',
      projectSummary: '',
      desiredOutcomes: '',
      collaborators: '',
      specificLocation: 'NO',
      locationDetails: '',
      projectRestrictions: '',
      availableResources: '',
      studentFunding: 'No Funding Available',
      fundingDetails: '',
      alternateContact: '',
      specialInstructions: '',
      proposalVideo: 'Proposal Video not Available',
      supplementalUpload: 'No supplemental file uploaded',
      projectStatus: '',
      closeStatus: 'Open',
      proposalID: '',
      applicantName: '',
      applicantEmail: '',
      applicantUIN: '',
      studentStatus: '',
      degreeProgram: '',
      experience: '',
      ableToMeetRequirements: '',
      unmetRequirements: '',
      proposalSummary: '',
      anticipatedOutcomes: '',
      resumeUpload: null,
      supplementalUpload: null,
    proposalStatus: '',
    applicationStatus: ''
  });

  const [proposals, setProposals] = useState([]); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null); 
  const [status, setStatus] = useState(null);
  const statusForUi = userType === 'Student' && status === 'Closed' ? 'Withdrawn' : status;
  const studentPrimaryActionLabel = useMemo(() => {
    if (!isStudentUser) return '';
    if (isStudentFormOpen) return 'Hide proposal form';
    if (statusForUi === 'Submitted') return 'Show Application';
    if (statusForUi === 'Saved') return 'Continue Application';
    if (statusForUi === 'Withdrawn') return 'Reapply';
    return 'Apply';
  }, [isStudentUser, isStudentFormOpen, statusForUi]);
  const [profileResumeUrl, setProfileResumeUrl] = useState('');
  const [profileDepartment, setProfileDepartment] = useState('');
  const projectKeywords = useMemo(() => joinKeywords(project?.keywords), [project]);
  
  const projectOverviewFields = useMemo(() => {
    const fields = [
      { label: 'Project Summary', value: project?.projectSummary },
      {
        label: 'Project Contact',
        value: (
          <>
            {project?.requesterTitle} {project?.requesterName}
            <br />
            <Link href={`mailto:${project?.requesterEmail}`} sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: '#500000' }}>
              {project?.requesterEmail}
            </Link>
          </>
        )
      },
      { label: 'Department', value: project?.requesterDepartment },
      { label: 'Proposal Deadline', value: formatDate(project?.proposalDeadline) },
      { label: 'Estimated Start Date', value: formatDate(project?.startDate) },
      { label: 'Estimated End Date', value: formatDate(project?.endDate) },
      { label: 'Keywords', value: projectKeywords },
      { label: 'Desired Outcomes', value: project?.desiredOutcomes },
      { label: 'Collaborators', value: project?.collaborators },
      { label: 'Available Resources', value: project?.availableResources },
      { label: 'Funding Details', value: project?.fundingDetails },
      { label: 'Project Restrictions', value: project?.projectRestrictions },
      { label: 'Special Instructions', value: project?.specialInstructions },
    ];

    if (project?.specificLocation === 'YES') {
      fields.splice(7, 0, {
        label: 'Location Details',
        value: project?.locationDetails,
      });
    }

    if (project?.proposalVideo) {
      fields.push({
        label: 'Project Video',
        value: (
          <Link href={project.proposalVideo} target="_blank" rel="noopener">
            View Project Video
          </Link>
        ),
      });
    }

    if (project?.supplementalUpload) {
      fields.push({
        label: 'Supplemental File',
        value: (
          <Link href={project.supplementalUpload} target="_blank" rel="noopener">
            View Supplemental File
          </Link>
        ),
      });
    }

    return fields;
  }, [project, projectKeywords]);
  
  let isStudent = userInfo?.userType === 'Student';

  const normalizeAuthToken = (rawToken) => {
    if (!rawToken) return '';
    return rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;
  };

  const buildAuthHeaders = (extra = {}) => {
    const rawToken = localStorage.getItem('token');
    if (!rawToken) {
      console.error('No auth token in localStorage');
      return null;
    }
    return { Authorization: normalizeAuthToken(rawToken), ...extra };
  };

  const fetchProposals = async (projectID) => {
    try {
      setLoading(true);
      const headers = buildAuthHeaders();
      if (!headers) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${endpointpath}/api/displayproposals?ProjectID=${projectID}`, {
        method: 'GET',
        headers,
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const data = await response.json();
      if (data.proposals && data.proposals.length > 0) {
        setProposals(data.proposals); 
      } else {
        setProposals([]); 
        if (isStudent) {
            setStatus(null);
        } else {
            setStatus(project?.projectStatus || null);
        } 
      }
      setLoading(false);
    } catch (err) {
      setError('Error fetching proposals');
      setLoading(false);
    }
  }; 

  const fetchProfileResume = async () => {
    if (!isStudent) return;
    const headers = buildAuthHeaders();
    if (!headers) {
      return;
    }

    try {
      const response = await fetch(`${endpointpath}/api/profile`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (data.resumeUrl) {
        setProfileResumeUrl(data.resumeUrl);
      }
      if (data.department) {
        setProfileDepartment(data.department);
        setFormData((prev) => ({
          ...prev,
          degreeProgram: prev.degreeProgram || data.department,
        }));
      }
    } catch (profileError) {
      console.error('Error fetching profile resume:', profileError);
    }
  };

   useEffect(() => {
    if (project?.projectID) {
      fetchProposals(project.projectID); 
    }
  }, [project]);
  
  useEffect(() => {
    if (project && proposals.length > 0) {
      const proposal = proposals[0]; 
      setFormData({
        projectID: project.projectID,
        requesterUIN: project.requesterUIN || '',
        requesterName: project.requesterName || '',
        requesterTitle: project.requesterTitle || '',
        requesterDepartment: project.requesterDepartment || '',
        requesterEmail: project.requesterEmail || '',
        requestTitle: project.requestTitle || '',
        proposalDeadline: project.proposalDeadline || '',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        keywords: project.keywords || '',
        projectSummary: project.projectSummary || '',
        desiredOutcomes: project.desiredOutcomes || '',
        collaborators: project.collaborators || '',
        specificLocation: project.specificLocation || 'NO',
        locationDetails: project.locationDetails || '',
        projectRestrictions: project.projectRestrictions || '',
        availableResources: project.availableResources || '',
        studentFunding: normalizeFunding(project.studentFunding) || 'No Funding Available',
        fundingDetails: project.fundingDetails || '',
        alternateContact: project.alternateContact || '',
        specialInstructions: project.specialInstructions || '',
        proposalVideo: project.proposalVideo || '',
        supplementalUpload: project.supplementalUpload || 'No supplemental file uploaded',
        projectStatus: project.projectStatus || '',
        closeStatus: project.closeStatus || '',

        proposalID: proposal?.proposalID || '',
        applicantName: resolveName(userInfo),
        applicantEmail: resolveEmail(userInfo),
        applicantUIN: userInfo.UIN || '',
        proposalTitle: proposal?.proposalTitle || '',
        studentStatus: proposal?.studentStatus || 'Undergraduate',
        degreeProgram: proposal?.degreeProgram || profileDepartment || '',
        experience: proposal?.experience || '',
        ableToMeetRequirements: proposal?.ableToMeetRequirements || 'YES',
        unmetRequirements: proposal?.unmetRequirements || '',
        proposalSummary: proposal?.proposalSummary || '',
        anticipatedOutcomes: proposal?.anticipatedOutcomes || '',
        resumeUpload: proposal?.resumeUpload || null,
        supplementalUpload: proposal?.supplementalUpload || null,
        applicationStatus: proposal?.applicationStatus || 'In Review',
        proposalStatus: proposal?.proposalStatus || ''
      });
  
      if (isStudent) {
        setStatus(proposal?.proposalStatus || null);
      } else {
        setStatus(project?.projectStatus || null);
      }

    } else if (project && proposals.length === 0) {
      setFormData({
        projectID: project.projectID,
        requesterUIN: project.requesterUIN || '',
        requesterName: project.requesterName || '',
        requesterTitle: project.requesterTitle || '',
        requesterDepartment: project.requesterDepartment || '',
        requesterEmail: project.requesterEmail || '',
        requestTitle: project.requestTitle || '',
        proposalDeadline: project.proposalDeadline || '',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        keywords: project.keywords || '',
        projectSummary: project.projectSummary || '',
        desiredOutcomes: project.desiredOutcomes || '',
        collaborators: project.collaborators || '',
        specificLocation: project.specificLocation || 'NO',
        locationDetails: project.locationDetails || '',
        projectRestrictions: project.projectRestrictions || '',
        availableResources: project.availableResources || '',
        studentFunding: normalizeFunding(project.studentFunding) || 'No Funding Available',
        fundingDetails: project.fundingDetails || '',
        alternateContact: project.alternateContact || '',
        specialInstructions: project.specialInstructions || '',
        proposalVideo: project.proposalVideo || '',
        supplementalUpload: project.supplementalUpload || 'No supplemental file uploaded',
        projectStatus: project.projectStatus || '',
        closeStatus: project.closeStatus || '',

        proposalID: '',
        applicantName: resolveName(userInfo),
        applicantEmail: resolveEmail(userInfo),
        applicantUIN: userInfo.UIN || '',
        proposalTitle: '',
        studentStatus: 'Undergraduate',
        degreeProgram: profileDepartment || '',
        experience: '',
        ableToMeetRequirements: 'YES',
        unmetRequirements: '',
        proposalSummary: '',
        anticipatedOutcomes: '',
        resumeUpload: null,
        supplementalUpload: null,
        applicationStatus: 'In Review',
        proposalStatus: ''
      });
  
      if (isStudent) {
        setStatus(null);
      } else {
        setStatus(project?.projectStatus || null);
      }
    }
  }, [project, proposals]); 

  useEffect(() => {
    if (userType === 'Student') {
      fetchProfileResume();
    }
  }, [userType]);

  useEffect(() => {
    if (
      isStudent &&
      profileResumeUrl &&
      (!formData.resumeUpload || formData.resumeUpload === 'No resume file uploaded')
    ) {
      setFormData((prev) => ({ ...prev, resumeUpload: profileResumeUrl }));
    }
  }, [profileResumeUrl, isStudent]);

  const navigate = useNavigate();
  const [videoFile, setVideoFile] = useState(null);
  const [formErrors, setFormErrors] = useState({}); 
  const [touchedFields, setTouchedFields] = useState({});
  const [isFormValid, setIsFormValid] = useState(false); 
  const [resumeFile, setResumeFile] = useState(null); 
  const [supplementalFile, setSupplementalFile] = useState(null); 
  const [notification, setNotification] = useState('');
  const [notificationType, setNotificationType] = useState(''); 

  const [dialogOpen, setDialogOpen] = useState(false); 
  const [dialogAction, setDialogAction] = useState(null); 

  const documentInputRef = useRef(null);

  const formatRemaining = (field, baseText) => {
    const limit = PROPOSAL_FIELD_LIMITS[field];
    if (!limit) return baseText;
    const length = (formData[field] || '').length;
    const remaining = Math.max(limit - length, 0);
    return `${baseText} (${remaining} characters remaining)`;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const limit = PROPOSAL_FIELD_LIMITS[name];
    const nextValue = typeof value === 'string' && limit ? value.slice(0, limit) : value;
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      const normalizedUserType = (userType || '').toLowerCase() === 'student' ? 'student' : 'employer';
      const validationError = validateUploadFile(name, file, normalizedUserType);
      if (validationError) {
        setNotification(validationError);
        setNotificationType('error');
        e.target.value = '';
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (name === 'proposalVideo') {
        setVideoFile(file);
        setFormData((prevFormData) => ({
          ...prevFormData,
          proposalVideo: URL.createObjectURL(file),
        }));
      } else if (name === 'resumeUpload') {
        setResumeFile(file);
        setFormData((prevFormData) => ({
          ...prevFormData,
          resumeUpload: URL.createObjectURL(file),
        }));
      } else if (name === 'supplementalUpload') {
        setSupplementalFile(file);
        setFormData((prevFormData) => ({
          ...prevFormData,
          supplementalUpload: URL.createObjectURL(file),
        }));
      }
    }
  };

  const handleFileRemove = () => {
    const fallbackResume = proposals[0]?.resumeUpload || profileResumeUrl || null;
    setFormData((prev) => ({ ...prev, resumeUpload: fallbackResume }));
    setResumeFile(null);
    if (documentInputRef.current) {
      documentInputRef.current.value = '';
    }
  };

const handleDeleteFile = () => {
  setFormData({ ...formData, supplementalUpload: null });
  setSupplementalFile(null);
  if (documentInputRef.current) {
    documentInputRef.current.value = ''; 
  }
};

  const getFileNameFromURL = (url) => {
    if (!url || typeof url !== 'string') return null;
    try {
      const parsed = new URL(url);
      const segments = parsed.pathname.split('/').filter(Boolean);
      if (segments.length === 0) return null;
      return decodeURIComponent(segments[segments.length - 1]);
    } catch (err) {
      return null;
    }
  };

  const resolveFileDisplayName = (fileRef, storedValue, fallbackLabel) => {
    if (fileRef && typeof fileRef.name === 'string' && fileRef.name.trim()) {
      return fileRef.name.trim();
    }
    if (typeof storedValue === 'string' && storedValue.trim()) {
      if (storedValue.startsWith('blob:')) {
        return fallbackLabel;
      }
      const parsed = getFileNameFromURL(storedValue);
      if (parsed) return parsed;
      return storedValue;
    }
    return '';
  };

const createFormPayload = (formData, videoFile, resumeFile, supplementalFile) => {
  const resolvedResume = formData.resumeUpload || profileResumeUrl || '';
  const effectiveData = {
    ...formData,
    applicantName: formData.applicantName || sidebarName,
    applicantEmail: formData.applicantEmail || sidebarEmail,
    applicantUIN: formData.applicantUIN || sidebarUIN,
    requesterName: formData.requesterName || sidebarName,
    requesterEmail: formData.requesterEmail || sidebarEmail,
    requesterUIN: formData.requesterUIN || sidebarUIN,
    resumeUpload: resolvedResume,
  };
  const formPayload = new FormData();
  for (const [key, value] of Object.entries(effectiveData)) {
    formPayload.append(key, value);
  }
  if (videoFile) {
    formPayload.append('proposalVideo', videoFile);
  }
  if (resumeFile) {
    formPayload.append('resumeUpload', resumeFile);
  }
  if (supplementalFile) {
    formPayload.append('supplementalUpload', supplementalFile);
  }
  return formPayload;
};

const openDialog = (action) => {
  setDialogAction(action); 
  setDialogOpen(true); 
};

const closeDialog = () => {
  setDialogOpen(false); 
};

const handleDialogConfirm = () => {
  if (dialogAction === 'cancel') {
    handleWithdraw(null, project, userType, userInfo); 
  } else if (dialogAction === 'close') {
    handleClose(null, project, userType, userInfo); 
  }
  setDialogOpen(false); 
};


const renderStudentForm = () => (
  <>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 'bold',
        backgroundColor: '#500000',
        color: '#ffffff',
        padding: '8px',
        mb: 2,
      }}
    >
      Proposal For
    </Typography>

    <Box
      sx={{
        border: '1px solid #c7c7c7',
        borderRadius: 1,
        backgroundColor: '#ffffff',
        px: 2.5,
        py: 1.6,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
        mb: 3,
      }}
    >
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f1f1f' }}>
        {project?.requestTitle || 'Project title unavailable'}
      </Typography>
    </Box>

    <Typography
      variant="h6"
      sx={{
        fontWeight: 'bold',
        backgroundColor: '#500000',
        color: '#ffffff',
        padding: '8px',
      }}
    >
      Student Information
    </Typography>

    <ReadOnlyField label="Name" value={sidebarName} />
    <ReadOnlyField label="Email" value={sidebarEmail} />

    <FormControl component="fieldset" sx={{ mt: 2 }}>
      <FormLabel sx={{ fontWeight: 600, color: '#500000' }}>Student Status</FormLabel>
      <RadioGroup
        row
        name="studentStatus"
        value={formData.studentStatus}
        onChange={handleChange}
        sx={{
          gap: { xs: 1.5, sm: 3 },
          flexWrap: 'wrap',
          '& .MuiFormControlLabel-root': {
            margin: 0,
          },
        }}
      >
        <FormControlLabel value="Undergraduate" control={<Radio />} label="Undergraduate" sx={{ mr: 0 }} />
        <FormControlLabel value="Graduate" control={<Radio />} label="Graduate" sx={{ mr: 0 }} />
      </RadioGroup>
    </FormControl>

    <StyledTextField
      label="Degree Program and Department"
      name="degreeProgram"
      fullWidth
      margin="normal"
      value={formData.degreeProgram}
      onChange={handleChange}
      helperText={formatRemaining('degreeProgram', 'Please list your degree program and department.')}
      inputProps={{ maxLength: PROPOSAL_FIELD_LIMITS.degreeProgram }}
      placeholder="For Ex: MS, Computer Science"
    />

    <StyledTextField
      label="Experience and Background"
      name="experience"
      fullWidth
      margin="normal"
      multiline
      rows={4}
      value={formData.experience}
      onChange={handleChange}
      helperText={formatRemaining('experience', 'Briefly explain your experience and background.')}
      inputProps={{ maxLength: PROPOSAL_FIELD_LIMITS.experience }}
      placeholder="For Ex: BTech in Computer Science; 3 years of Computer Vision experience"
    />

    <Typography
      variant="h6"
      sx={{
        fontWeight: 'bold',
        backgroundColor: '#500000',
        color: '#ffffff',
        padding: '8px',
        mt: 3,
      }}
    >
      Proposal Details
    </Typography>

    <StyledTextField
      label="Proposal Title"
      name="proposalTitle"
      fullWidth
      margin="normal"
      value={formData.proposalTitle}
      onChange={handleChange}
      helperText={formatRemaining('proposalTitle', 'Please provide a proposal title.')}
      inputProps={{ maxLength: PROPOSAL_FIELD_LIMITS.proposalTitle }}
      placeholder="For Ex: Image generation using RAG Pipeline"
    />

    <FormControl component="fieldset" sx={{ mt: 2 }}>
      <FormLabel sx={{ fontWeight: 600, color: '#500000' }}>
        Able to Meet Project Requirements?
      </FormLabel>
      <RadioGroup
        row
        name="ableToMeetRequirements"
        value={formData.ableToMeetRequirements}
        onChange={handleChange}
        sx={{
          gap: { xs: 1.5, sm: 4 },
          flexWrap: 'wrap',
          '& .MuiFormControlLabel-root': {
            margin: 0,
          },
        }}
      >
        <FormControlLabel value="YES" control={<Radio />} label="Yes" sx={{ mr: 0 }} />
        <FormControlLabel value="NO" control={<Radio />} label="No" sx={{ mr: 0 }} />
        <FormControlLabel value="MAYBE" control={<Radio />} label="Maybe" sx={{ mr: 0 }} />
      </RadioGroup>
    </FormControl>

    {formData.ableToMeetRequirements !== 'YES' && (
      <StyledTextField
        label="Details on Unmet Requirements"
        name="unmetRequirements"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={formData.unmetRequirements}
        onChange={handleChange}
        helperText={formatRemaining('unmetRequirements', 'Explain which requirements cannot be met.')}
        inputProps={{ maxLength: PROPOSAL_FIELD_LIMITS.unmetRequirements }}
        placeholder="For Ex: I am unable to meet the project's requirements due to limited resources and expertise in the specific technologies required."
      />
    )}

    <StyledTextField
      label="Proposal Summary"
      name="proposalSummary"
      fullWidth
      margin="normal"
      multiline
      rows={6}
      value={formData.proposalSummary}
      onChange={handleChange}
      helperText={formatRemaining('proposalSummary', 'Provide a summary of your intended project.')}
      inputProps={{ maxLength: PROPOSAL_FIELD_LIMITS.proposalSummary }}
      placeholder="For Ex: This proposal aims to leverage the Retrieval-Augmented Generation (RAG) pipeline for advanced image generation. By integrating retrieval mechanisms with generative models, we seek to enhance the quality and relevance of generated images. The approach promises improved contextual accuracy and creative outputs for various applications in digital content creation."
    />

    <StyledTextField
      label="Anticipated Products and Outcomes"
      name="anticipatedOutcomes"
      fullWidth
      margin="normal"
      multiline
      rows={4}
      value={formData.anticipatedOutcomes}
      onChange={handleChange}
      helperText={formatRemaining('anticipatedOutcomes', 'Describe the expected outcomes at the end of the project.')}
      inputProps={{ maxLength: PROPOSAL_FIELD_LIMITS.anticipatedOutcomes }}
      placeholder="For Ex: High-quality, contextually accurate images for marketing and digital platforms, with enhanced creativity and personalization for various industries."
    />

    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          backgroundColor: '#500000',
          color: '#ffffff',
          padding: '8px',
        }}
      >
        Upload Resume (.pdf)
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, mb: 1, color: 'rgba(80,0,0,0.7)' }}>
        Maximum file size: 1 MB
      </Typography>
      {profileResumeUrl && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Profile resume on file:{' '}
          <Link href={profileResumeUrl} target="_blank" rel="noopener noreferrer">
            View profile resume
          </Link>
        </Typography>
      )}
      <input
        type="file"
        name="resumeUpload"
        accept="application/pdf"
        onChange={handleFileChange}
      />

      {formData.resumeUpload && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Typography>
            {resolveFileDisplayName(resumeFile, formData.resumeUpload, 'Resume ready to upload')}
          </Typography>
          <IconButton onClick={handleFileRemove} sx={{ ml: 1 }}>
            <DeleteIcon />
          </IconButton>
        </Box>
      )}

      {formData.resumeUpload && typeof formData.resumeUpload === 'string' && (
        <Link
          href={formData.resumeUpload}
          target="_blank"
          rel="noopener"
          sx={{ mt: 1, display: 'block' }}
        >
          View Uploaded Resume
        </Link>
      )}
    </Box>

    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          backgroundColor: '#500000',
          color: '#ffffff',
          padding: '8px',
        }}
      >
        Supplemental Material (optional)
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
        Attach any additional supporting files such as PDFs, slide decks, or videos that strengthen your proposal.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(80,0,0,0.7)' }}>
        Maximum file size: 10 MB
      </Typography>
      <input
        type="file"
        name="supplementalUpload"
        ref={documentInputRef}
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,image/*"
        onChange={handleFileChange}
      />

      {formData.supplementalUpload && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Typography>
            {resolveFileDisplayName(
              supplementalFile,
              formData.supplementalUpload,
              'Supplemental file ready to upload'
            )}
          </Typography>
          <IconButton onClick={handleDeleteFile} sx={{ ml: 1 }}>
            <DeleteIcon />
          </IconButton>
        </Box>
      )}

      {formData.supplementalUpload && typeof formData.supplementalUpload === 'string' && (
        <Link
          href={formData.supplementalUpload}
          target="_blank"
          rel="noopener"
          sx={{ mt: 1, display: 'block' }}
        >
          View Supplemental File
        </Link>
      )}
    </Box>
  </>
);

const renderFormContent = () => (
  <>
    {notification && (
      <Box
        padding={2}
        bgcolor={notificationType === 'success' ? '#732f2f' : '#3c0000'}
        color="#ffffff"
        textAlign="center"
        marginBottom={2}
        sx={{ borderRadius: 1 }}
      >
        {notification}
      </Box>
    )}
    <Typography
      variant="h5"
      sx={{
        fontWeight: 'bold',
        color: '#500000',
        textAlign: 'center',
        marginBottom: 2,
      }}
    >
      {userType === 'Student' ? 'Proposal Form' : 'Project Form'}
    </Typography>
    <form>
      {userType === 'Student' ? renderStudentForm() : (
        <>
          {/* Employer form content remains unchanged here */}
        </>
      )}
    </form>

    <Box display="flex" justifyContent="space-between" padding={2}>
      {userType === 'Employer' && statusForUi === 'Submitted' && (
        <Button
          onClick={() => openDialog('cancel')}
          sx={buttonStyles.cancelProject}
          variant="contained"
          disabled={!project || !project.projectID}
        >
          Cancel Project
        </Button>
      )}
      {statusForUi !== 'Submitted' && (
        <Button onClick={handleSave} sx={buttonStyles.save} variant="contained">Save</Button>
      )}
      {statusForUi !== 'Submitted' ? (
        <Button onClick={handleSubmit} sx={buttonStyles.publish} variant="contained">
          {userType === 'Student' ? 'Submit' : 'Update'}
        </Button>
      ) : null}
      {statusForUi === 'Submitted' && (
        <Button
          onClick={() => openDialog('close')}
          sx={buttonStyles.close}
          variant="contained"
          disabled={!project || !project.projectID}
        >
          {userType === 'Student' ? 'Withdraw Application' : 'Close Project'}
        </Button>
      )}
    </Box>
  </>
);
const handleSubmit = async (e) => {
    e.preventDefault();

    const formPayload = createFormPayload(formData, videoFile, resumeFile, supplementalFile);
    console.log(formPayload);

    try {
      const headers = buildAuthHeaders({
        usertype: (userType || '').toLowerCase(),
      });
      if (!headers) {
        alert('Session expired: please log in again.');
        return;
      }

      const response = await fetch(`${endpointpath}/api/submit`, {
        method: 'POST',
        body: formPayload,
        headers,
      });

      if (response.ok) {
        console.log('Form submitted successfully');
        console.log(formData);
        if (userType === 'Employer') {
          setNotification('Project submitted successfully.');
        } else {
          setNotification('Application submitted successfully.');
        }
        setNotificationType('success');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        handleStudentFormClose();
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const data = await parseApiErrorResponse(response);
        const errorMessage = data?.error || data?.message || 'Failed to submit form.';
        console.error('Failed to submit form:', errorMessage);
        setNotification(errorMessage);
        setNotificationType('error');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setNotification(error.message || 'Failed to submit form.');
      setNotificationType('error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const formPayload = createFormPayload(formData, videoFile, resumeFile, supplementalFile);
    console.log(formPayload);

    try {
      const headers = buildAuthHeaders({
        usertype: (userType || '').toLowerCase(),
      });
      if (!headers) {
        alert('Session expired: please log in again.');
        return;
      }

      const response = await fetch(`${endpointpath}/api/save`, {
        method: 'POST',
        body: formPayload,
        headers,
      });

      if (response.ok) {
        console.log('Form draft edited successfully');
        console.log(formData);
        if (userType === 'Employer') {
          setNotification('Project saved successfully.');
        } else {
          setNotification('Application saved successfully.');
        }
        setNotificationType('success');

        window.scrollTo({ top: 0, behavior: 'smooth' });
        handleStudentFormClose();
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const data = await parseApiErrorResponse(response);
        const errorMessage = data?.error || data?.message || 'Failed to save form.';
        console.error('Failed to save form:', errorMessage);
        setNotification(errorMessage);
        setNotificationType('error');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setNotification(error.message || 'Failed to save form.');
      setNotificationType('error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleWithdraw = async (e, project, userType, userInfo) => {
    if (e) {
      e.preventDefault(); 
    }

    try {
      const headers = buildAuthHeaders({
        'Content-Type': 'application/json',
      });
      if (!headers) {
        alert('Session expired: please log in again.');
        return;
      }

      const response = await fetch(`${endpointpath}/api/withdraw`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          projectID: project.projectID,
          userType,
          UIN: userInfo?.UIN || '',
          applicantUIN: sidebarUIN,
          applicantEmail: sidebarEmail,
        }),
      });

      if (response.ok) {
        if (userType === 'Employer') {
          setNotification('Project cancelled successfully.');
        } else {
          setNotification('Application withdrawn successfully.');
        }
        setNotificationType('success');

        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setNotification('Failed to withdraw/cancel.');
        setNotificationType('error');
      }
    } catch (error) {
      setNotification('Failed to withdraw/cancel.');
      setNotificationType('error');
    } finally {
      handleStudentFormClose(); 
    }
  };

  const handleClose = async (e, project, userType, userInfo) => {
    if (e) {
      e.preventDefault(); 
    }

    try {
      const headers = buildAuthHeaders({
        'Content-Type': 'application/json',
      });
      if (!headers) {
        alert('Session expired: please log in again.');
        return;
      }

      const response = await fetch(`${endpointpath}/api/close`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          projectID: project.projectID,
          userType,
          UIN: userInfo?.UIN || '',
          applicantUIN: sidebarUIN,
          applicantEmail: sidebarEmail,
        }),
      });

      if (response.ok) {
        setNotification('Project closed successfully.');
        setNotificationType('success');

        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setNotification('Failed to close project.');
        setNotificationType('error');
      }
    } catch (error) {
      setNotification('Failed to close project.');
      setNotificationType('error');
    } finally {
      handleStudentFormClose(); 
    }
  };

  return (
    <>
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      minHeight={0}
      alignItems="flex-start"
      justifyContent="flex-start"
      sx={{ width: '100%' }}
    >
            <Box
              display={notification ? 'block' : 'none'}
              padding={2}
              bgcolor={notificationType === 'success' ? '#732f2f' : '#3c0000'}
              color="#ffffff"
              textAlign="center"
              marginBottom={2}
            >
              {notification}
            </Box>

            <Box
              className="project-details-container"
              sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flex: 1,
                  minHeight: 0,
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Box
                  sx={{
                    flex: '1 1 0',
                    minWidth: 0,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    pr: { xs: 0, lg: 1 },
                  }}
                >
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {project.requestTitle}
                  </Typography>
                  <Box
                    my={3}
                    sx={
                      userType === 'Employer'
                        ? {
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                          }
                        : { display: 'grid', gap: 2 }
                    }
                  >
                    {userType === 'Employer' ? (
                      projectOverviewFields.map((field, index) => (
                        <ReadOnlyField
                          key={field.label || index}
                          label={field.label}
                          value={field.value}
                        />
                      ))
                    ) : (
                      projectOverviewFields
                        .filter((field) => field.value)
                        .map((field) => (
                          <ReadOnlyField key={field.label} label={field.label} value={field.value} />
                        ))
                    )}
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={handleClick}
                    sx={{
                      backgroundColor:
                        statusForUi === 'Submitted'
                          ? '#732f2f'
                          : statusForUi === 'Saved'
                            ? '#e9e4dc'
                            : 'transparent',
                      color:
                        statusForUi === 'Submitted'
                          ? '#ffffff'
                          : statusForUi === 'Saved'
                            ? '#500000'
                            : '#500000',
                      border: '2px solid #500000',
                      margin: statusForUi !== 'Submitted' && statusForUi !== 'Saved' ? '8px' : '0',
                      '&:hover': {
                        backgroundColor:
                          statusForUi === 'Submitted'
                            ? '#3c0000'
                            : statusForUi === 'Saved'
                              ? '#e9e4dc'
                              : 'transparent',
                      },
                    }}
                  >
                    {userType === 'Student'
                      ? studentPrimaryActionLabel
                      : statusForUi === 'Submitted'
                        ? 'Show Request'
                        : statusForUi === 'Saved'
                          ? 'Edit Request'
                          : statusForUi === 'Withdrawn'
                            ? 'Edit Request'
                            : 'Edit Request'}
                  </Button>

                  {userType === 'Employer' && project?.projectID && (
                    <Button
                      variant="contained"
                      onClick={handleViewProposals}
                      sx={{
                        backgroundColor: '#e9e4dc',
                        color: '#500000',
                        fontWeight: 700,
                        border: '2px solid #500000',
                        ml: 1,
                        '&:hover': { backgroundColor: '#ded7cc' },
                      }}
                    >
                      View Proposals
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Proposal list moved to Proposal Viewer page for employers */}


            {isEditPage && (
              <Box
                sx={{
                  width: '100%',
                  padding: 2,
                  borderLeft: '5px solid #500000',
                }}
              >
                {renderFormContent()}
              </Box>
            )}

    </Box>

    {isStudentUser &&
      isStudentFormOpen &&
      !isEditPage &&
      proposalPortalMount &&
      ReactDOM.createPortal(
        <Box
          role="dialog"
          aria-modal={false}
          aria-label="Proposal application"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#ffffff',
            pointerEvents: 'auto',
            overflow: 'auto',
            padding: 2,
            borderLeft: '5px solid #500000',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {renderFormContent()}
        </Box>,
        proposalPortalMount
      )}

    <Dialog open={dialogOpen} onClose={closeDialog}>
    <DialogTitle sx={{ color: '#500000' }}>Warning</DialogTitle>
    <DialogContent>
      <Typography>
        You are about to {dialogAction === 'cancel' ? 'cancel this project' : 'close this project'}. Please confirm your action.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={closeDialog} sx={{ color: '#535353' }}>
        Cancel
      </Button>
      <Button
        onClick={handleDialogConfirm}
        sx={{ color: '#ffffff', backgroundColor: '#3c0000', '&:hover': { backgroundColor: '#500000' } }}
      >
        Confirm
      </Button>
    </DialogActions>
    </Dialog>

</>
  );
};

export default ProjectDetails;

const buttonStyles = {
  cancelProject: {
    border: '3px solid #3e3e3e', 
    backgroundColor: '#3e3e3e', 
    color: '#ffffff', 
    fontWeight: 'bold',
  },
  save: {
    border: '3px solid #500000', 
    backgroundColor: '#e9e4dc', 
    color: '#000000', 
    fontWeight: 'bold',
  },
  publish: {
    border: '3px solid #500000', 
    backgroundColor: '#732f2f', 
    color: '#000000', 
    fontWeight: 'bold',
  },
  close: {
    border: '3px solid #500000', 
    backgroundColor: '#500000', 
    color: '#ffffff', 
    fontWeight: 'bold',
  },
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& label.Mui-focused': {
    color: '#500000',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '& fieldset': {
      borderColor: '#c7c7c7',
    },
    '&:hover fieldset': {
      borderColor: '#500000',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#500000',
      borderWidth: '2px',
    },
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'grey',
    fontStyle: 'italic',
  },
}));
